"use client";

import React, { useState, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ChevronDown, Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { getMajorCitiesByCountry, type City } from "@/lib/services/city-service";
import {
  getUserCountry,
  getCountryIso2ForCities,
  type Country,
} from "@/lib/services/country-service";

interface CityPickerProps {
  value?: string; // City name
  onChange: (cityName: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  countryCode?: string; // Filter cities by country code (e.g., "EG")
  /** Used with static country JSON (no cca2): ISO2 is read from flag URLs when `countryCode` is missing */
  country?: Country | null;
  autoDetect?: boolean;
}

export default function CityPicker({
  value,
  onChange,
  placeholder,
  label,
  error,
  disabled,
  className,
  countryCode,
  country,
  autoDetect = true,
}: CityPickerProps) {
  const t = useTranslations("general");
  const locale = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const resolvedIso2 =
    countryCode?.trim().toUpperCase() || getCountryIso2ForCities(country) || undefined;
  const [isDetecting, setIsDetecting] = useState(autoDetect && !resolvedIso2);

  // Load cities when country changes — cancel in-flight loads so an older request cannot overwrite the list
  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      setSearchQuery("");
      setIsLoading(true);

      try {
        let code =
          countryCode?.trim().toUpperCase() ||
          getCountryIso2ForCities(country) ||
          undefined;

        if (!code && autoDetect) {
          setIsDetecting(true);
          try {
            const userCountry = await getUserCountry();
            if (userCountry) {
              code =
                userCountry.cca2?.trim().toUpperCase() ||
                getCountryIso2ForCities(userCountry) ||
                undefined;
            }
          } finally {
            if (!cancelled) {
              setIsDetecting(false);
            }
          }
        } else {
          setIsDetecting(false);
        }

        if (cancelled) {
          return;
        }

        if (!code) {
          setCities([]);
          setFilteredCities([]);
          return;
        }

        const majorCities = getMajorCitiesByCountry(code);
        if (cancelled) {
          return;
        }
        setCities(majorCities);
        setFilteredCities(majorCities);
      } catch (error) {
        console.error("Error loading cities:", error);
        if (!cancelled) {
          setCities([]);
          setFilteredCities([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCities();

    return () => {
      cancelled = true;
    };
  }, [countryCode, country, autoDetect]);

  // Filter within the current country's list only (stays in sync when `cities` reloads)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCities(cities);
      return;
    }

    const q = searchQuery.toLowerCase().trim();
    const filtered = cities.filter(
      (city) =>
        city.name.toLowerCase().includes(q) ||
        (city.nameAr && city.nameAr.toLowerCase().includes(q))
    );
    setFilteredCities(filtered);
  }, [searchQuery, cities]);

  const handleSelectCity = (city: City) => {
    onChange(city.name);
    setIsOpen(false);
    setSearchQuery("");
  };

  const displayValue = value || "";
  const displayPlaceholder = isDetecting
    ? t("detecting_location") || "Detecting location..."
    : placeholder || t("select_city") || "Select your city";

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="text-sm font-medium text-primary-foreground font-poppins">
          {label}
        </label>
      )}
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled || isLoading || isDetecting}
        className={cn(
          "w-full h-12 rounded-full bg-black/50 border border-white/20 text-primary-foreground hover:bg-black/70 hover:text-white px-6 py-3 flex items-center justify-between transition-colors text-left",
          error && "border-red-500",
          (disabled || isLoading || isDetecting) && "opacity-50 cursor-not-allowed"
        )}
      >
        {isLoading || isDetecting ? (
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm font-poppins">{displayPlaceholder}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <MapPin className="w-4 h-4 flex-shrink-0 text-primary" />
            <span className={cn(
              "text-sm font-poppins truncate",
              !displayValue && "text-primary-foreground/50"
            )}>
              {displayValue || displayPlaceholder}
            </span>
          </div>
        )}
        <ChevronDown className="w-4 h-4 opacity-70 flex-shrink-0" />
      </button>

      {/* City Picker Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md max-h-[80vh] bg-black/95 border-white/20 [&_*]:text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-poppins">
              {t("select_city") || "Select City"}
            </DialogTitle>
          </DialogHeader>
          
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
            <Input
              type="text"
              placeholder={t("search") || "Search cities..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/20 text-white placeholder:text-white/50"
            />
          </div>

          {/* Cities List */}
          <ScrollArea className="h-[400px] mt-4">
            <div className="space-y-1 [&_button]:text-white">
              {filteredCities.map((city) => {
                const isSelected = value === city.name;
                
                return (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => handleSelectCity(city)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left text-white",
                      isSelected && "bg-primary/20 border border-primary/30"
                    )}
                    style={{ color: 'white' }}
                  >
                    <MapPin className="w-4 h-4 flex-shrink-0 text-primary" />
                    <span className="flex-1 text-white font-poppins transition-colors" style={{ color: 'white' }}>
                      {city.name}
                    </span>
                    {city.isCapital && (
                      <span className="text-xs text-primary/70 font-poppins">
                        {t("capital") || "Capital"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
            {(!isLoading && filteredCities.length === 0) && (
              <div className="text-center py-12 text-white/60">
                {t("no_cities_found") || "No cities found"}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

